import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { RelationalContextMap } from '../../../../../models/RelationalContextMap.model';
import { RelationalFlagsContext } from '../../../../../store/RelationalFlagsContext';
import { RelationalGroupService } from '../../../../../services/RelationalGroup.service';
import { useDebounce } from '../../../../../hooks';
import { ApiResult, PaginationModel } from '../../../../../models';
import { DynamicDispatchAlert, emptyGuid } from '../../../../../util';
import { translate } from './ListRelationalGroup.translations';

const ListRelationalGroupFunctions = (): any => {
  const dispatch = useDispatch();
  const [list, setList] = useState<RelationalContextMap[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    relationalContextMap,
    setRelationalGroup,
    language,
    relationalGroup,
    filter,
    refresh,
  } = useContext(RelationalFlagsContext);
  const [pagination, setPagination] = useState<PaginationModel>(
    new PaginationModel({
      take: 20,
      total: 0,
      skip: 1,
    } as PaginationModel),
  );
  const scrollObserver = useRef<any>();
  const debouncedFilter: any = useDebounce(filter, 700);

  useEffect(() => {
    if (list?.length >= pagination.total) {
      return;
    } else {
      if (scrollObserver?.current) {
        const intersectionObserver = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setPagination({ ...pagination, skip: pagination.skip + 1 });
          }
        });
        intersectionObserver.observe(scrollObserver?.current);
        return () => intersectionObserver.disconnect();
      } else {
        return;
      }
    }
  }, [list]);

  const handleGetList = async (
    newFilter?: string,
    update?: boolean,
  ): Promise<any> => {
    setIsLoading(true);
    let result = null as unknown as ApiResult;
    if (newFilter || !!update) {
      result = await RelationalGroupService.getAllRelationalGroup(
        relationalContextMap.mapContextRelationalId || emptyGuid(),
        language,
        debouncedFilter,
        pagination.take,
        1,
      );
    } else {
      result = await RelationalGroupService.getAllRelationalGroup(
        relationalContextMap.mapContextRelationalId || emptyGuid(),
        language,
        debouncedFilter,
        pagination.take,
        pagination.skip,
      );
    }
    if (result) {
      if (newFilter || !!update) {
        setPagination({ ...pagination, skip: 1, total: result.data.total });
        setList(result.data.records);
      } else {
        setPagination({ ...pagination, total: result.data.total });
        setList((listData) => [...listData, ...result.data.records]);
      }
      if (result.errorMessage) {
        DynamicDispatchAlert(dispatch, result.message, 'ERROR', translate);
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    // Adiciona na lista
    if (pagination.skip > 1) {
      handleGetList();
    }
  }, [pagination.skip]);

  useEffect(() => {
    // Zera a lista
    handleGetList(debouncedFilter, refresh);
  }, [debouncedFilter, refresh, relationalContextMap.mapContextRelationalId]);

  const handleGetGroupId = useCallback(
    async (id: string) => {
      if (id) {
        setIsLoading(true);
        RelationalGroupService.getGroupId(id, language)
          .then((apiResult: ApiResult<any>) => {
            if (apiResult.success) {
              if (apiResult.data) {
                setRelationalGroup(apiResult.data);
              }
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    },
    [relationalGroup?.groupRelationalId, language],
  );

  useEffect(() => {
    if (
      !relationalGroup?.groupRelationalId &&
      relationalGroup?.groupRelationalId !== emptyGuid()
    ) {
      if (list && list.length > 0) {
        const newList = list?.map((_) => {
          return {
            ..._,
            isSelected: (_.isSelected = false),
          };
        }) as any[];
        setList(newList);
      }
    }
  }, [relationalGroup]);

  const onOpen = (item: any): void => {
    if (item.isSelected === false) {
      setRelationalGroup({} as any);
      return;
    }
    handleGetGroupId(item?.groupRelationalId);
  };

  return {
    isLoading,
    relationalGroup,
    list,
    setList,
    onOpen,
    pagination,
    scrollObserver,
  };
};

export { ListRelationalGroupFunctions };
